import { useEffect, useRef } from 'react';

import {
  beginPanelOutsideCloseGesture,
  endPanelOutsideCloseGesture,
  isPanelOutsideCloseGestureActive,
} from './panelOutsideCloseGesture';

/** Fallback when no click follows mousedown (e.g. drag-away). Must exceed click dispatch. */
const OUTSIDE_CLOSE_FALLBACK_MS = 300;

/**
 * Headless UI portals its menus to `document.body`, so a menu opened from inside a panel is not
 * a DOM descendant of that panel and `isOutsidePanelClick` reads it as an outside click. Left
 * unguarded, picking a menu item closes the panel AND strands the menu — the capture-phase
 * `mousedown` below is swallowed, so Headless UI never learns the item was chosen and never
 * runs its own close.
 *
 * Every panel needs this. `SidePanel` merges it in for its consumers; the two components that
 * call this hook directly pass it themselves.
 */
export const portaledMenuExcludeSelectors = ['[role="menu"]'];

function isOutsidePanelClick(
  target: Node,
  panel: HTMLDivElement | null,
  excludeSelectors?: string[]
): boolean {
  if (panel?.contains(target)) {
    return false;
  }

  if (excludeSelectors?.length) {
    const element = target as Element;
    for (const selector of excludeSelectors) {
      if (element.closest(selector)) {
        return false;
      }
    }
  }

  return true;
}

function finishOutsideCloseGesture(
  onClose: (() => void) | undefined,
  onOpenChange: (open: boolean) => void
) {
  endPanelOutsideCloseGesture();
  onClose?.();
  onOpenChange(false);
}

export interface UsePanelBehaviorParams {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enableClickOutside?: boolean;
  excludeSelectors?: string[];
  /** callback to execute when panel closes */
  onClose?: () => void;
}

export function usePanelBehavior({
  open,
  onOpenChange,
  enableClickOutside = true,
  excludeSelectors,
  onClose,
}: UsePanelBehaviorParams) {
  const panelRef = useRef<HTMLDivElement>(null);
  const fallbackCloseIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onCloseRef = useRef(onClose);
  const onOpenChangeRef = useRef(onOpenChange);
  const excludeSelectorsRef = useRef(excludeSelectors);
  const enableClickOutsideRef = useRef(enableClickOutside);

  onCloseRef.current = onClose;
  onOpenChangeRef.current = onOpenChange;
  excludeSelectorsRef.current = excludeSelectors;
  enableClickOutsideRef.current = enableClickOutside;

  useEffect(() => {
    if (!open) {
      endPanelOutsideCloseGesture();
      return;
    }

    const clearFallbackClose = () => {
      if (fallbackCloseIdRef.current !== null) {
        clearTimeout(fallbackCloseIdRef.current);
        fallbackCloseIdRef.current = null;
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearFallbackClose();
        endPanelOutsideCloseGesture();
        onCloseRef.current?.();
        onOpenChangeRef.current(false);
      }
    };

    const handleMouseDownOutside = (event: MouseEvent) => {
      if (!enableClickOutsideRef.current) {
        return;
      }

      const target = event.target as Node;
      if (!isOutsidePanelClick(target, panelRef.current, excludeSelectorsRef.current)) {
        return;
      }

      clearFallbackClose();
      beginPanelOutsideCloseGesture();
      // Capture phase: block D3 zoom / canvas handlers from seeing this gesture.
      event.stopPropagation();
      event.preventDefault();
      // Close on click capture; long fallback if click never arrives (setTimeout(0) is too early).
      fallbackCloseIdRef.current = setTimeout(() => {
        fallbackCloseIdRef.current = null;
        if (!isPanelOutsideCloseGestureActive()) {
          return;
        }
        finishOutsideCloseGesture(onCloseRef.current, onOpenChangeRef.current);
      }, OUTSIDE_CLOSE_FALLBACK_MS);
    };

    const handleClickSuppress = (event: MouseEvent) => {
      if (!isPanelOutsideCloseGestureActive()) {
        return;
      }

      clearFallbackClose();
      event.stopImmediatePropagation();
      event.preventDefault();
      finishOutsideCloseGesture(onCloseRef.current, onOpenChangeRef.current);
    };

    if (enableClickOutsideRef.current) {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('mousedown', handleMouseDownOutside, true);
      document.addEventListener('click', handleClickSuppress, true);
    }

    return () => {
      clearFallbackClose();
      endPanelOutsideCloseGesture();
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDownOutside, true);
      document.removeEventListener('click', handleClickSuppress, true);
    };
    // Callbacks and exclude selectors are read from refs so filter apply re-renders
    // do not tear down listeners mid-gesture.
  }, [open]);

  return { panelRef };
}
